import { performanceStore, type PerformanceSnapshot } from "./store";

const TELEMETRY_ENDPOINT = "/api/playback-telemetry";
const SAMPLE_INTERVAL_MS = 10_000;

const sessionId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().replaceAll("-", "");
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
};

const scalarMetrics = (snapshot: PerformanceSnapshot) => Object.fromEntries(
  Object.entries(snapshot).filter(([, value]) =>
    value === null || ["string", "number", "boolean"].includes(typeof value)
  ),
);

export function startPlaybackTelemetry() {
  const id = sessionId();
  const startedAt = Date.now();
  let stopped = false;

  const payload = (event: string, endedAt?: number) => ({
    sessionId: id,
    startedAt,
    capturedAt: Date.now(),
    ...(endedAt === undefined ? {} : { endedAt }),
    event,
    visible: document.visibilityState === "visible",
    metrics: scalarMetrics(performanceStore.getSnapshot()),
  });

  const send = (event: string, useBeacon = false, endedAt?: number) => {
    const body = JSON.stringify(payload(event, endedAt));
    if (useBeacon && "sendBeacon" in navigator) {
      navigator.sendBeacon(
        TELEMETRY_ENDPOINT,
        new Blob([body], { type: "application/json" }),
      );
      return;
    }
    void fetch(TELEMETRY_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => { /* The next sample retries without interrupting playback. */ });
  };

  const onVisibilityChange = () => {
    send(document.visibilityState === "visible" ? "visible" : "hidden");
  };
  const onPageHide = () => {
    if (!stopped) send("pagehide", true, Date.now());
  };

  send("start");
  const timer = window.setInterval(() => send("sample"), SAMPLE_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);

  return () => {
    if (stopped) return;
    stopped = true;
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
    send("stop", true, Date.now());
  };
}
